import CampanaNotificaciones from "./CampanaNotificaciones";
import BotonInstructivo from "./BotonInstructivo";
import BotonMapaProceso from "./BotonMapaProceso";
import { useState, useEffect, useRef, useCallback } from "react";
import { subirDatos, eliminarDato } from "./syncSupabase";
import { generarOCdesde } from "./ComprasUtils";
const ST = {
  PALLETS: "alqos_stock_pallets",
  MOVS: "alqos_stock_movimientos",
  OTS: "alqos_ots",
  CONFIG: "alqos_config_materiales",
};
const storage = {
  get: (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } },
  set: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};
// Mismo criterio que IngresoMateriales.js (14/08): Cartón macizo viene por
// calibre, no por gramaje -- es el mismo dato interno (campo "gramaje"),
// pero acá donde se muestra hay que rotularlo como calibre, no como g/m²,
// porque es solo un dato de espesor.
const TIPOS_CALIBRE = ["Cartón macizo"];
const etiquetaGramaje = (tipoMaterial, valor) => {
  if (!valor) return "";
  return TIPOS_CALIBRE.includes(tipoMaterial) ? `${valor} cal.` : `${valor}g/m²`;
};
// NOTA (auditoría Fase 3 Paso 3): antes esto hacía DELETE de toda la tabla +
// INSERT de lo que hubiera en localStorage en cada guardado -- con dos
// dispositivos usando Stock al mismo tiempo, el segundo guardado podía borrar
// pallets que el otro acababa de crear. Ahora usa subirDatos()/eliminarDato()
// (upsert incremental por id), igual que el resto de los módulos.
const C = {
  navy: "#09184D", purple: "#7B5BF2", blue: "#2A7FFF",
  green: "#22c55e", red: "#e63946", amber: "#f59e0b",
};
const estadoPalletColor = {
  disponible: "#22c55e", en_uso: "#2A7FFF", agotado: "#94a3b8", reservado: "#f59e0b",
};
const estadoPalletLabel = {
  disponible: "Disponible", en_uso: "En uso", agotado: "Agotado", reservado: "Reservado",
};
// Mismo fix que proximoNumero() en IngresoMateriales.js (auditoría 13/08):
// actuales.length+1 repite codigoPallet apenas la lista local está
// incompleta (caché vieja, filtro, etc.) -- se busca el número más alto ya
// usado por prefijo y se sigue desde ahí.
function proximoNumeroPallet(codigos, prefijo) {
  const re = new RegExp(`^${prefijo}-\\d{4}-(\\d+)$`);
  let maxNum = 0;
  codigos.forEach(c => {
    const m = re.exec(c || "");
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
  });
  return maxNum + 1;
}
export function integrarStockDesdeIngreso(ingreso, usuario) {
  if (!ingreso || !["aceptado", "aceptado_condicionado"].includes(ingreso.estado)) return null;
  const actuales = storage.get(ST.PALLETS);
  const nuevo = {
    id: Date.now(),
    codigoPallet: "PAL-" + new Date().getFullYear() + "-" + String(proximoNumeroPallet(actuales.map(p=>p.codigoPallet), "PAL")).padStart(4, "0"),
    tipoMaterial: ingreso.tipoMaterial,
    proveedor: ingreso.proveedor,
    // El ingreso ya trae el remito cargado (IngresoMateriales.js) -- antes se
    // perdía acá, así que este pallet "automático" (el que se genera cuando
    // no se cargó ningún pallet a mano, o al aceptar un material que estaba
    // en cuarentena) quedaba siempre sin remito.
    remito: ingreso.remito || "",
    lote: ingreso.lote || "",
    gramaje: ingreso.gramaje || "",
    medida: ingreso.medidaPliego || "",
    unidad: ingreso.unidad,
    cantidadInicial: parseFloat(ingreso.cantidad),
    cantidadActual: parseFloat(ingreso.cantidad),
    esFSC: ingreso.esFSC || false,
    estado: ingreso.estado === "aceptado_condicionado" ? "reservado" : "disponible",
    codigoIngreso: ingreso.codigoIngreso,
    fechaIngreso: ingreso.fecha,
    creadoPor: usuario?.nombre || "Sistema",
    fechaCreacion: new Date().toISOString(),
    autoGenerado: true,
    historial: [],
  };
  const nuevos = [...actuales, nuevo];
  storage.set(ST.PALLETS, nuevos);
  subirDatos(ST.PALLETS);
  return nuevo.codigoPallet;
}
// ─── Lógica de reposición (Industria 4.0 / no depender de que alguien se acuerde) ───
// Exportada para que NotificadorALQOS.js pueda generar la alerta automática
// con la misma cuenta exacta que se ve en pantalla — una sola fuente de verdad.
export function calcularEstadoReposicion(tipoMaterial, pallets, movs, configs) {
  const config = configs.find(c => c.tipoMaterial === tipoMaterial);
  if (!config || !config.critico) return null;
  const stockActual = pallets
    .filter(p => p.tipoMaterial === tipoMaterial && p.estado !== "agotado")
    .reduce((acc, p) => acc + parseFloat(p.cantidadActual ?? p.cantidadInicial ?? 0), 0);
  let puntoReorden = 0;
  let consumoSemanal = 0;
  if (config.modoCalculo === "automatico") {
    const hace4Semanas = Date.now() - 28 * 24 * 60 * 60 * 1000;
    const movsRecientes = movs.filter(m => m.tipoMaterial === tipoMaterial && new Date(m.fecha).getTime() >= hace4Semanas);
    const totalConsumido = movsRecientes.reduce((acc, m) => acc + parseFloat(m.cantidad || 0), 0);
    consumoSemanal = totalConsumido / 4;
    const tiempoReposicionSemanas = (parseFloat(config.tiempoReposicionDias) || 7) / 7;
    const buffer = consumoSemanal * tiempoReposicionSemanas * 0.2; // 20% de colchón de seguridad
    puntoReorden = consumoSemanal * tiempoReposicionSemanas + buffer;
  } else {
    puntoReorden = parseFloat(config.stockMinimo) || 0;
  }
  let estado = "ok";
  if (stockActual <= puntoReorden) estado = "critico";
  else if (stockActual <= puntoReorden * 1.3) estado = "alerta";
  return { tipoMaterial, stockActual, puntoReorden, consumoSemanal, estado, config };
}
export function calcularTodasLasAlertasReposicion(pallets, movs, configs) {
  const tipos = [...new Set(configs.filter(c => c.critico).map(c => c.tipoMaterial))];
  return tipos
    .map(t => calcularEstadoReposicion(t, pallets, movs, configs))
    .filter(Boolean)
    .filter(a => a.estado !== "ok");
}
// ─── OC automática de reposición (14/08) ───
// Estados de la OC tal como los define Compras.js (no se re-exportan desde
// ahí, así que se repite acá el mismo mapeo para mostrar el estado real de
// la OC en la pantalla de Reposición -- "la devolución que faltaba").
const ESTADOS_OC_REPOSICION = {
  pendiente:  { label: "⏳ OC pendiente de aprobación", color: "#f59e0b" },
  aprobada:   { label: "✅ OC aprobada",                color: "#22c55e" },
  en_proceso: { label: "🔵 OC en proceso",              color: "#3b82f6" },
  recibida:   { label: "📦 OC recibida",                color: "#808085" },
  rechazada:  { label: "🔴 OC rechazada",               color: "#e63946" },
};
export default function StockMateriales({ onVolver, oscuro, usuario }) {
  const [pallets, setPallets] = useState([]);
  const [movs, setMovs] = useState([]);
  const [ots, setOts] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seccion, setSeccion] = useState("remitos");
  // Grupo elegido en la vista "Por remito" (Pantalla 2 = desglose de ese
  // grupo). null = se muestra la Pantalla 1 (grupos).
  const [grupoSeleccionado, setGrupoSeleccionado] = useState(null);
  // ── Estado de la pantalla "Por remito" (buscador propio, resumen por
  // material, orden y toggles para ocultar/mostrar cada bloque) --
  // deliberadamente separado del "busqueda" de la pestaña Pallets, para que
  // no se pisen los dos buscadores.
  const [busquedaRemitos, setBusquedaRemitos] = useState("");
  const [busquedaResumen, setBusquedaResumen] = useState("");
  const [ordenRemitos, setOrdenRemitos] = useState("desc"); // "desc" = mayor a menor
  const [resumenAbierto, setResumenAbierto] = useState(true);
  const [remitosAbierto, setRemitosAbierto] = useState(true);
  const eliminarPallet = async (id) => {
    const nuevos = pallets.filter(p => p.id !== id);
    setPallets(nuevos);
    storage.set(ST.PALLETS, nuevos);
    await eliminarDato(ST.PALLETS, id);
  };
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalDescuento, setModalDescuento] = useState(null);
  const [modalRotulo, setModalRotulo] = useState(null);
  const [modalConfig, setModalConfig] = useState(null);
  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : C.navy;
  const sub = oscuro ? "#9aabd4" : "#808085";
  const card = { background: cardBg, borderRadius: "12px", padding: "1.2rem 1.5rem", border: "1px solid " + borde, marginBottom: "10px" };
  const inp = { width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid " + borde, fontSize: "13px", boxSizing: "border-box", background: cardBg, color: texto };
  const esAdmin = usuario?.rol === "Administrador";
  useEffect(() => {
    async function cargar() {
      try {
        const { supabase } = await import("./supabase");
        const [{ data: dp }, { data: dm }, { data: dc }] = await Promise.all([
          supabase.from("stock_pallets").select("data"),
          supabase.from("stock_movimientos").select("data"),
          supabase.from("config_materiales").select("data"),
        ]);
        if (dp?.length) { const lp = dp.map(r => r.data); setPallets(lp); storage.set(ST.PALLETS, lp); }
        else setPallets(storage.get(ST.PALLETS));
        if (dm?.length) { const lm = dm.map(r => r.data); setMovs(lm); storage.set(ST.MOVS, lm); }
        else setMovs(storage.get(ST.MOVS));
        if (dc?.length) { const lc = dc.map(r => r.data); setConfigs(lc); storage.set(ST.CONFIG, lc); }
        else setConfigs(storage.get(ST.CONFIG));
      } catch {
        setPallets(storage.get(ST.PALLETS));
        setMovs(storage.get(ST.MOVS));
        setConfigs(storage.get(ST.CONFIG));
      }
      setOts(storage.get(ST.OTS));
      setCargando(false);
    }
    cargar();
  }, []);
  const crearPallet = useCallback((form) => {
    const actuales = storage.get(ST.PALLETS);
    const nuevo = {
      id: Date.now(),
      codigoPallet: "PAL-" + new Date().getFullYear() + "-" + String(proximoNumeroPallet(actuales.map(p=>p.codigoPallet), "PAL")).padStart(4, "0"),
      tipoMaterial: form.tipoMaterial,
      proveedor: form.proveedor,
      remito: form.remito || "",
      lote: form.lote || "",
      gramaje: form.gramaje || "",
      medida: form.medida || "",
      unidad: form.unidad,
      cantidadInicial: parseFloat(form.cantidadInicial),
      cantidadActual: parseFloat(form.cantidadInicial),
      esFSC: form.esFSC || false,
      estado: "disponible",
      codigoIngreso: form.codigoIngreso || "",
      fechaIngreso: form.fechaIngreso,
      creadoPor: usuario?.nombre || "Sistema",
      fechaCreacion: new Date().toISOString(),
      historial: [],
    };
    const nuevos = [...actuales, nuevo];
    storage.set(ST.PALLETS, nuevos);
    setPallets(nuevos);
    subirDatos(ST.PALLETS);
    setModalNuevo(false);
  }, [usuario]);
  const descontarStock = useCallback((palletId, descuento) => {
    const actualesPallets = storage.get(ST.PALLETS);
    const p = actualesPallets.find(x => x.id === palletId);
    if (!p) return;
    const nuevaCantidad = Math.max(0, parseFloat(p.cantidadActual ?? p.cantidadInicial) - descuento.cantidad);
    const nuevoEstado = nuevaCantidad === 0 ? "agotado" : "en_uso";
    const actualizados = actualesPallets.map(x => x.id === palletId
      ? { ...x, cantidadActual: nuevaCantidad, estado: nuevoEstado }
      : x
    );
    storage.set(ST.PALLETS, actualizados);
    setPallets(actualizados);
    subirDatos(ST.PALLETS);
    const actualesMovs = storage.get(ST.MOVS);
    const mov = {
      id: Date.now(),
      codigoMov: "MOV-" + Date.now(),
      palletId, codigoPallet: p.codigoPallet,
      tipoMaterial: p.tipoMaterial, proveedor: p.proveedor,
      motivo: descuento.motivo, otId: descuento.otId || "",
      cantidad: descuento.cantidad, unidad: p.unidad,
      cantidadAntes: parseFloat(p.cantidadActual ?? p.cantidadInicial),
      cantidadDespues: nuevaCantidad,
      por: usuario?.nombre || "Sistema",
      fecha: new Date().toISOString(),
    };
    const nuevosMovs = [...actualesMovs, mov];
    storage.set(ST.MOVS, nuevosMovs);
    setMovs(nuevosMovs);
    subirDatos(ST.MOVS);
    setModalDescuento(null);
  }, [usuario]);
  const guardarConfigTipo = useCallback((tipoMaterial, campos) => {
    const actuales = storage.get(ST.CONFIG);
    const existente = actuales.find(c => c.tipoMaterial === tipoMaterial);
    let nuevos;
    if (existente) {
      nuevos = actuales.map(c => c.tipoMaterial === tipoMaterial ? { ...c, ...campos, ultimoCambio: new Date().toISOString(), cambiadoPor: usuario?.nombre } : c);
    } else {
      nuevos = [...actuales, {
        id: Date.now(),
        tipoMaterial,
        critico: false,
        modoCalculo: "fijo",
        stockMinimo: 0,
        tiempoReposicionDias: 7,
        ...campos,
        creadoPor: usuario?.nombre || "Sistema",
        fechaCreacion: new Date().toISOString(),
      }];
    }
    storage.set(ST.CONFIG, nuevos);
    setConfigs(nuevos);
    subirDatos(ST.CONFIG);
    setModalConfig(null);
  }, [usuario]);
  // ── OC de reposición → Compras (automática, con opción manual) ──
  // generarOCReposicion() es la función única que arma la OC -- la usa tanto
  // el useEffect de abajo (disparo 100% automático apenas un material cae en
  // 🔴 "Reponer ya") como el botón manual "🛒 Generar OC ahora" en la tarjeta
  // de cada material (por si se quiere adelantar la compra estando en 🟡, o
  // por cualquier motivo puntual). Deja registrado quién marcó el material
  // como crítico y qué OT fue la última que lo consumió -- "no se olvida
  // quién lo pidió". La OC nace en estado "pendiente": el disparo puede ser
  // automático o manual, pero la aprobación sigue siendo 100% humana
  // (Administrador o rol Compras, ver Compras.js) -- vigilado por Compras.
  const generarOCReposicion = useCallback((cfg, estado) => {
    const ultimaOT = [...movs].reverse().find(m => m.tipoMaterial === cfg.tipoMaterial && m.motivo === "OT" && m.otId);
    const unidadMaterial = pallets.find(p => p.tipoMaterial === cfg.tipoMaterial)?.unidad || "unidades";
    const cantidadSugerida = Math.max(1, Math.ceil(estado.puntoReorden - estado.stockActual));
    const codigo = generarOCdesde({
      origen: "Reposicion",
      referencia: cfg.tipoMaterial,
      usuario,
      descripcion: `Reposición de ${cfg.tipoMaterial} — stock actual ${estado.stockActual} ${unidadMaterial}, por debajo del punto de reposición (${Math.round(estado.puntoReorden)} ${unidadMaterial}).`,
      categoria: "Materia prima",
      extra: {
        proveedor: cfg.proveedorHabitual || "",
        cantidad: cantidadSugerida,
        unidad: unidadMaterial,
        urgencia: estado.estado === "critico" ? "critico" : "urgente",
        otVinculada: ultimaOT?.otId || "",
        observaciones: `Generada por el sistema de reposición de Stock de Materiales. Marcado como crítico por ${cfg.cambiadoPor || cfg.creadoPor || "Sistema"}.` + (ultimaOT?.otId ? ` Última OT que consumió este material: ${ultimaOT.otId}.` : ""),
      },
    });
    if (!codigo) return;
    const actuales = storage.get(ST.CONFIG);
    const actualizadas = actuales.map(c => c.tipoMaterial === cfg.tipoMaterial ? { ...c, ocVinculada: codigo } : c);
    storage.set(ST.CONFIG, actualizadas);
    setConfigs(actualizadas);
    subirDatos(ST.CONFIG);
  }, [pallets, movs, usuario]);
  // Disparo automático: recorre los materiales críticos y genera la OC sola
  // apenas alguno cae en 🔴, sin esperar a que nadie la pida a mano. Cuando
  // el stock se recupera o la OC llega a "recibida", se desvincula sola para
  // poder disparar una OC nueva la próxima vez que haga falta.
  useEffect(() => {
    if (cargando) return;
    let huboLimpieza = false;
    const configsLimpias = configs.map(cfg => {
      if (!cfg.critico || !cfg.ocVinculada) return cfg;
      const estado = calcularEstadoReposicion(cfg.tipoMaterial, pallets, movs, configs);
      const compras = storage.get("alqos_compras");
      const compra = compras.find(c => c.codigo === cfg.ocVinculada);
      const resuelto = !estado || estado.estado === "ok" || !compra || compra.estado === "recibida";
      if (resuelto) { huboLimpieza = true; return { ...cfg, ocVinculada: null }; }
      return cfg;
    });
    if (huboLimpieza) {
      storage.set(ST.CONFIG, configsLimpias);
      setConfigs(configsLimpias);
      subirDatos(ST.CONFIG);
      return; // se re-evalúa en el próximo render con los datos ya limpios
    }
    const pendienteDeOC = configs.find(cfg => cfg.critico && !cfg.ocVinculada && calcularEstadoReposicion(cfg.tipoMaterial, pallets, movs, configs)?.estado === "critico");
    if (pendienteDeOC) {
      const estado = calcularEstadoReposicion(pendienteDeOC.tipoMaterial, pallets, movs, configs);
      generarOCReposicion(pendienteDeOC, estado);
    }
  }, [cargando, pallets, movs, configs, generarOCReposicion]);
  const exportarCSV = () => {
    const headers = ["Codigo","Tipo","Proveedor","Lote","Unidad","Inicial","Actual","Estado","FSC"];
    const rows = pallets.map(p => [p.codigoPallet, p.tipoMaterial, p.proveedor, p.lote || "", p.unidad, p.cantidadInicial, p.cantidadActual ?? p.cantidadInicial, p.estado, p.esFSC ? "SI" : ""]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "Stock_" + new Date().toISOString().slice(0,10) + ".csv"; a.click();
    URL.revokeObjectURL(url);
  };
  const palletsFiltrados = pallets.filter(p => {
    const matchE = filtroEstado === "todos" || p.estado === filtroEstado;
    const matchB = !busqueda || [p.codigoPallet, p.tipoMaterial, p.proveedor, p.lote].join(" ").toLowerCase().includes(busqueda.toLowerCase());
    return matchE && matchB;
  });
  const kpis = {
    total: pallets.length,
    disponibles: pallets.filter(p => p.estado === "disponible").length,
    en_uso: pallets.filter(p => p.estado === "en_uso").length,
    agotados: pallets.filter(p => p.estado === "agotado").length,
    fsc: pallets.filter(p => p.esFSC).length,
  };
  const tiposUnicos = [...new Set(pallets.map(p => p.tipoMaterial))].sort();
  const alertasReposicion = calcularTodasLasAlertasReposicion(pallets, movs, configs);
  // ── Agrupación "Por remito" ──
  // Clave: remito + proveedor + material + gramaje + medida (NO solo remito
  // -- un mismo remito puede traer varias combinaciones distintas de
  // material+gramaje+medida en la misma entrega, ej. Suzano trayendo
  // 330g/72x100 junto con otras medidas en la misma factura -- agrupar solo
  // por remito sumaría kilos de productos que en realidad son distintos.
  // Se suma proveedor también porque el número de remito lo pone cada
  // proveedor, no es un ID único de ALQOS -- dos proveedores distintos
  // podrían compartir el mismo número por coincidencia).
  const claveGrupo = (p) => [p.remito?.trim() || "Sin remito", p.proveedor||"", p.tipoMaterial||"", p.gramaje||"", p.medida||""].join("|");
  const gruposPorRemito = (() => {
    const mapa = new Map();
    pallets.forEach(p => {
      const key = claveGrupo(p);
      if (!mapa.has(key)) {
        mapa.set(key, {
          key, remito: p.remito?.trim() || "Sin remito", proveedor: p.proveedor||"",
          tipoMaterial: p.tipoMaterial||"", gramaje: p.gramaje||"", medida: p.medida||"",
          unidad: p.unidad||"", cantidadPallets: 0, totalInicial: 0, totalActual: 0, esFSC: false,
        });
      }
      const g = mapa.get(key);
      g.cantidadPallets += 1;
      g.totalInicial += parseFloat(p.cantidadInicial)||0;
      g.totalActual += parseFloat(p.cantidadActual ?? p.cantidadInicial)||0;
      if (p.esFSC) g.esFSC = true;
    });
    return Array.from(mapa.values()).sort((a,b) => (a.remito==="Sin remito") - (b.remito==="Sin remito") || a.remito.localeCompare(b.remito));
  })();
  const palletsDelGrupo = grupoSeleccionado ? pallets.filter(p => claveGrupo(p) === grupoSeleccionado.key) : [];
  // ── Resumen por material ──
  // Agrupa TODOS los grupos de remito (sin importar remito ni proveedor) por
  // material+gramaje+medida+unidad -- responde directo "cuánto tengo de este
  // material puntual", que es lo que se pidió: no un total ciego por unidad
  // (mezclaría cartulina 330g con cartulina 250g con cartón macizo solo por
  // compartir "kg"), sino un dato accionable por combinación real.
  const resumenPorMaterial = (() => {
    const mapa = new Map();
    gruposPorRemito.forEach(g => {
      const key = [g.tipoMaterial, g.gramaje, g.medida, g.unidad].join("|");
      if (!mapa.has(key)) {
        mapa.set(key, { key, tipoMaterial: g.tipoMaterial, gramaje: g.gramaje, medida: g.medida, unidad: g.unidad, actual: 0, inicial: 0, pallets: 0 });
      }
      const m = mapa.get(key);
      m.actual += g.totalActual;
      m.inicial += g.totalInicial;
      m.pallets += g.cantidadPallets;
    });
    return Array.from(mapa.values()).sort((a,b) => b.actual - a.actual);
  })();
  const resumenFiltrado = !busquedaResumen.trim()
    ? resumenPorMaterial
    : resumenPorMaterial.filter(m => [m.tipoMaterial, m.gramaje, m.medida].join(" ").toLowerCase().includes(busquedaResumen.trim().toLowerCase()));
  // ── Filtro + orden de la lista de remitos ──
  const gruposFiltrados = (() => {
    const q = busquedaRemitos.trim().toLowerCase();
    const filtrados = !q ? gruposPorRemito : gruposPorRemito.filter(g =>
      [g.remito, g.proveedor, g.tipoMaterial, g.gramaje, g.medida].join(" ").toLowerCase().includes(q)
    );
    return [...filtrados].sort((a,b) => ordenRemitos === "desc" ? b.totalActual - a.totalActual : a.totalActual - b.totalActual);
  })();
  // Total agregado de lo filtrado en "Por remito", separado por unidad para
  // no sumar cosas que no se pueden sumar (kg + pliegos + litros mezclados).
  const totalFiltradoPorUnidad = {};
  gruposFiltrados.forEach(g => { totalFiltradoPorUnidad[g.unidad] = (totalFiltradoPorUnidad[g.unidad]||0) + g.totalActual; });
  // Tarjeta de pallet -- misma UI de siempre, ahora reutilizada tanto por la
  // pestaña "Pallets" (sin cambios) como por el desglose de "Por remito".
  const renderPalletCard = (p) => {
    const actual = parseFloat(p.cantidadActual ?? p.cantidadInicial);
    const pct = Math.round((actual / p.cantidadInicial) * 100);
    const barColor = pct > 50 ? C.green : pct > 20 ? C.amber : C.red;
    return (
      <div key={p.id} style={{ ...card, border: "1px solid " + (estadoPalletColor[p.estado] + "44") }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
          <div>
            <span style={{ fontWeight: "700", color: C.purple, fontSize: "13px" }}>{p.codigoPallet}</span>
            {" "}<span style={{ fontWeight: "700", color: texto }}>{p.tipoMaterial}</span>
            {p.esFSC && <span style={{ marginLeft: "6px", fontSize: "10px", color: "#15803d", background: "#dcfce7", padding: "2px 6px", borderRadius: "6px", fontWeight: "700" }}>🌿 FSC</span>}
            <p style={{ fontSize: "12px", color: sub, margin: "3px 0 0" }}>{p.proveedor}{p.lote ? " · Lote " + p.lote : ""}</p>
          </div>
          <span style={{ fontSize: "11px", fontWeight: "700", color: estadoPalletColor[p.estado] }}>{estadoPalletLabel[p.estado]}</span>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: sub, marginBottom: "4px" }}>
            <span>Stock</span>
            <span style={{ fontWeight: "700", color: barColor }}>{actual} / {p.cantidadInicial} {p.unidad} ({pct}%)</span>
          </div>
          <div style={{ height: "6px", background: oscuro ? "#1e3a8a" : "#f1f5f9", borderRadius: "3px" }}>
            <div style={{ height: "100%", width: pct + "%", background: barColor, borderRadius: "3px" }}/>
          </div>
        </div>
        <div style={{ display: "flex", gap: "6px" }}>
          <button onClick={() => setModalDescuento(p)} style={{ padding: "5px 12px", background: C.red + "15", border: "1px solid " + C.red + "40", color: C.red, borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>− Descontar</button>
          <button onClick={() => setModalRotulo(p)} style={{ padding: "5px 12px", background: C.purple + "15", border: "1px solid " + C.purple + "40", color: C.purple, borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>🖨️ Rótulo</button>
          {p.estado !== "reservado" && p.estado !== "agotado" && (
            <button onClick={() => { const actuales = storage.get(ST.PALLETS); const act = actuales.map(x => x.id === p.id ? {...x, estado: "reservado"} : x); storage.set(ST.PALLETS, act); setPallets(act); subirDatos(ST.PALLETS); }} style={{ padding: "5px 12px", background: "#f59e0b15", border: "1px solid #f59e0b40", color: "#f59e0b", borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>🟡 Reservar</button>
          )}
          {p.estado === "reservado" && (
            <button onClick={() => { const actuales = storage.get(ST.PALLETS); const act = actuales.map(x => x.id === p.id ? {...x, estado: "disponible"} : x); storage.set(ST.PALLETS, act); setPallets(act); subirDatos(ST.PALLETS); }} style={{ padding: "5px 12px", background: "#22c55e15", border: "1px solid #22c55e40", color: "#22c55e", borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>✅ Liberar</button>
          )}
          <button onClick={() => { if(window.confirm('¿Eliminar este pallet?')) eliminarPallet(p.id); }} style={{ padding: "5px 12px", background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>🗑 Eliminar</button>
        </div>
      </div>
    );
  };
  if (cargando) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: bg }}>
      <p style={{ color: oscuro ? "white" : C.navy }}>Cargando stock...</p>
    </div>
  );
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={onVolver} style={{ background: "rgba(255,255,255,.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Menú</button>
          <BotonInstructivo moduloKey="stock_materiales" oscuro={oscuro} />
          {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="stock_materiales" oscuro={oscuro} />}
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>📦 Stock de Materiales</span>
          {alertasReposicion.length > 0 && (
            <span style={{ background: "#e63946", color: "white", fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "12px" }}>
              🔴 {alertasReposicion.length} a reponer
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={exportarCSV} style={{ padding: "6px 12px", background: "rgba(34,197,94,.2)", border: "1px solid rgba(34,197,94,.4)", color: "#86efac", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>⬇️ CSV</button>
          <CampanaNotificaciones oscuro={oscuro} modulo="Stock" />
          <button onClick={() => setModalNuevo(true)} style={{ padding: "6px 14px", background: C.purple, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "13px", fontWeight: "700" }}>+ Nuevo pallet</button>
        </div>
      </div>
      <div style={{ display: "flex", borderBottom: "1px solid " + borde, background: cardBg, padding: "0 1.5rem" }}>
        {[["remitos","🧾 Por remito"],["pallets","📦 Pallets"],["movimientos","📋 Movimientos"],["inventario","📊 Inventario"],["reposicion","🎯 Reposición"]].map(([k,label]) => (
          <button key={k} onClick={() => { setSeccion(k); setGrupoSeleccionado(null); }} style={{ padding: "12px 18px", border: "none", borderBottom: seccion === k ? "3px solid " + C.purple : "3px solid transparent", background: "none", cursor: "pointer", fontSize: "13px", fontWeight: seccion === k ? "700" : "500", color: seccion === k ? C.purple : sub, position: "relative" }}>
            {label}
            {k === "reposicion" && alertasReposicion.length > 0 && (
              <span style={{ position: "absolute", top: "6px", right: "2px", width: "8px", height: "8px", borderRadius: "50%", background: "#e63946" }}/>
            )}
          </button>
        ))}
      </div>
      <div style={{ padding: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px,1fr))", gap: "10px", marginBottom: "1.5rem" }}>
          {[["Pallets", kpis.total, C.purple],["Disponibles", kpis.disponibles, C.green],["En uso", kpis.en_uso, C.blue],["Agotados", kpis.agotados, "#94a3b8"],["FSC", kpis.fsc, "#15803d"]].map(([lab, val, col]) => (
            <div key={lab} style={{ ...card, textAlign: "center", marginBottom: 0, padding: "1rem" }}>
              <p style={{ fontSize: "24px", fontWeight: "800", color: col, margin: 0 }}>{val}</p>
              <p style={{ fontSize: "11px", color: sub, margin: "4px 0 0" }}>{lab}</p>
            </div>
          ))}
        </div>
        {seccion === "remitos" && (
          grupoSeleccionado ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "1rem", flexWrap: "wrap" }}>
                <button onClick={() => setGrupoSeleccionado(null)} style={{ background: cardBg, border: "1px solid " + borde, color: texto, padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>← Volver a remitos</button>
                <div>
                  <span style={{ fontWeight: "700", color: C.purple, fontSize: "14px" }}>{grupoSeleccionado.remito}</span>
                  <span style={{ marginLeft: "8px", color: texto, fontWeight: "600", fontSize: "13px" }}>{grupoSeleccionado.tipoMaterial}</span>
                  {(grupoSeleccionado.gramaje || grupoSeleccionado.medida) && (
                    <span style={{ marginLeft: "8px", color: sub, fontSize: "12px" }}>
                      {etiquetaGramaje(grupoSeleccionado.tipoMaterial, grupoSeleccionado.gramaje)}{grupoSeleccionado.gramaje && grupoSeleccionado.medida ? " · " : ""}{grupoSeleccionado.medida || ""}
                    </span>
                  )}
                </div>
              </div>
              {palletsDelGrupo.length === 0
                ? <div style={{ textAlign: "center", padding: "3rem" }}><p style={{ color: texto }}>No quedan pallets en este grupo.</p></div>
                : palletsDelGrupo.map(p => renderPalletCard(p))
              }
            </>
          ) : (
            <>
              {/* Resumen por material -- detalle real, no total ciego mezclado por unidad */}
              <input placeholder="🔎 Buscar dentro del resumen... (ej: cartulina, 330, 72x100)" value={busquedaResumen} onChange={e => setBusquedaResumen(e.target.value)} style={{ ...inp, marginBottom: "8px" }}/>
              <div onClick={() => setResumenAbierto(v => !v)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", cursor: "pointer" }}>
                <h3 style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: C.navy }}>
                  📊 Resumen por material ({busquedaResumen.trim() ? `${resumenFiltrado.length} de ${resumenPorMaterial.length}` : resumenPorMaterial.length})
                </h3>
                <span style={{ fontSize: "12px", color: sub }}>{resumenAbierto ? "▲ ocultar" : "▼ mostrar"}</span>
              </div>
              {resumenAbierto && (
                <div style={{ background: cardBg, border: "1px solid " + borde, borderRadius: "12px", marginBottom: "18px", overflow: "hidden" }}>
                  {resumenFiltrado.length === 0
                    ? <div style={{ padding: "12px 14px", fontSize: "12px", color: sub }}>Sin materiales que coincidan con "{busquedaResumen}".</div>
                    : resumenFiltrado.map((m, i) => (
                        <div key={m.key} onClick={() => setBusquedaRemitos([m.tipoMaterial, m.gramaje, m.medida].filter(Boolean).join(" "))}
                          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 14px", borderBottom: i < resumenFiltrado.length - 1 ? "1px solid " + (oscuro ? "#1e3a8a" : "#f3f4f6") : "none", fontSize: "12px", cursor: "pointer" }}>
                          <span>
                            <span style={{ fontWeight: "700", color: texto }}>{m.tipoMaterial}</span>
                            <span style={{ color: sub, fontSize: "11px", marginLeft: "6px" }}>{m.gramaje ? etiquetaGramaje(m.tipoMaterial, m.gramaje) + " · " : ""}{m.medida || ""}</span>
                          </span>
                          <span>
                            <span style={{ fontWeight: "800", color: C.purple, whiteSpace: "nowrap" }}>{m.actual.toLocaleString("es-AR")} {m.unidad}</span>
                            <span style={{ color: sub, fontSize: "11px", marginLeft: "8px" }}>· {m.pallets} pallet{m.pallets > 1 ? "s" : ""}</span>
                          </span>
                        </div>
                      ))
                  }
                </div>
              )}

              {/* Buscador + orden de la lista de remitos */}
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                <input placeholder="Buscar material, gramaje, medida, proveedor o remito..." value={busquedaRemitos} onChange={e => setBusquedaRemitos(e.target.value)} style={{ ...inp, flex: 1, minWidth: "220px" }}/>
                <button onClick={() => setOrdenRemitos(o => o === "desc" ? "asc" : "desc")} style={{ padding: "8px 14px", borderRadius: "8px", border: "1px solid " + borde, background: cardBg, fontSize: "12px", fontWeight: "700", color: sub, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {ordenRemitos === "desc" ? "⬇️ Mayor a menor" : "⬆️ Menor a mayor"}
                </button>
              </div>

              {/* Total agregado del filtro actual, separado por unidad */}
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: "10px", padding: "10px 16px", fontSize: "12px", color: "#15803d", fontWeight: "700", marginBottom: "14px" }}>
                {gruposFiltrados.length === 0
                  ? "Sin resultados para ese filtro."
                  : `Total de lo filtrado: ${Object.entries(totalFiltradoPorUnidad).map(([u,v]) => `${v.toLocaleString("es-AR")} ${u}`).join(" · ")} — en ${gruposFiltrados.reduce((a,g)=>a+g.cantidadPallets,0)} pallets, ${gruposFiltrados.length} grupo${gruposFiltrados.length>1?'s':''}`
                }
              </div>

              {/* Toggle para ocultar/mostrar la lista de remitos, mismo patrón que el resumen */}
              <div onClick={() => setRemitosAbierto(v => !v)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px", cursor: "pointer" }}>
                <h3 style={{ margin: 0, fontSize: "13px", fontWeight: "700", color: C.navy }}>🧾 Remitos ({gruposFiltrados.length})</h3>
                <span style={{ fontSize: "12px", color: sub }}>{remitosAbierto ? "▲ ocultar" : "▼ mostrar"}</span>
              </div>
              {remitosAbierto && (
                gruposFiltrados.length === 0
                  ? <div style={{ textAlign: "center", padding: "3rem" }}><p style={{ fontSize: "40px" }}>🧾</p><p style={{ color: texto }}>{pallets.length === 0 ? "Sin pallets — hacé clic en + Nuevo pallet" : "Sin remitos que coincidan con ese filtro"}</p></div>
                  : gruposFiltrados.map(g => (
                      <div key={g.key} onClick={() => setGrupoSeleccionado(g)} style={{ ...card, cursor: "pointer" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px" }}>
                          <div>
                            <span style={{ fontWeight: "700", color: C.purple, fontSize: "14px" }}>{g.remito}</span>
                            {g.esFSC && <span style={{ marginLeft: "6px", fontSize: "10px", color: "#15803d", background: "#dcfce7", padding: "2px 6px", borderRadius: "6px", fontWeight: "700" }}>🌿 FSC</span>}
                            <p style={{ fontSize: "13px", fontWeight: "600", color: texto, margin: "4px 0 0" }}>{g.tipoMaterial}{g.gramaje ? ` · ${etiquetaGramaje(g.tipoMaterial, g.gramaje)}` : ""}{g.medida ? ` · ${g.medida}` : ""}</p>
                            <p style={{ fontSize: "12px", color: sub, margin: "2px 0 0" }}>{g.proveedor} · {g.cantidadPallets} pallet{g.cantidadPallets > 1 ? "s" : ""}</p>
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <p style={{ fontSize: "11px", color: sub, margin: 0 }}>Actual / Inicial</p>
                            <p style={{ fontSize: "17px", fontWeight: "800", color: C.green, margin: "2px 0 0" }}>{g.totalActual} {g.unidad}</p>
                            <p style={{ fontSize: "12px", color: sub, margin: 0 }}>de {g.totalInicial} {g.unidad}</p>
                          </div>
                        </div>
                      </div>
                    ))
              )}
            </>
          )
        )}
        {seccion === "pallets" && (
          <>
            <input placeholder="Buscar..." value={busqueda} onChange={e => setBusqueda(e.target.value)} style={{ ...inp, marginBottom: "10px" }}/>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginBottom: "1rem" }}>
              {["todos", "disponible", "en_uso", "agotado", "reservado"].map(v => (
                <button key={v} onClick={() => setFiltroEstado(v)} style={{ padding: "4px 10px", borderRadius: "16px", fontSize: "11px", cursor: "pointer", fontWeight: "600", border: filtroEstado === v ? "2px solid " + C.purple : "1px solid " + borde, background: filtroEstado === v ? "rgba(123,91,242,.1)" : cardBg, color: filtroEstado === v ? C.purple : sub }}>
                  {v === "todos" ? "Todos" : estadoPalletLabel[v]}
                </button>
              ))}
            </div>
            {palletsFiltrados.length === 0
              ? <div style={{ textAlign: "center", padding: "4rem" }}><p style={{ fontSize: "40px" }}>📦</p><p style={{ color: texto }}>Sin pallets — hacé clic en + Nuevo pallet</p></div>
              : palletsFiltrados.map(p => renderPalletCard(p))
            }
          </>
        )}
        {seccion === "movimientos" && (
          <>
            {movs.length === 0
              ? <div style={{ textAlign: "center", padding: "4rem" }}><p style={{ fontSize: "40px" }}>📋</p><p style={{ color: texto }}>Sin movimientos</p></div>
              : [...movs].reverse().map(m => (
                <div key={m.id} style={card}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <span style={{ fontWeight: "700", fontSize: "12px", color: C.purple }}>{m.codigoMov}</span>
                      {" · "}<span style={{ fontWeight: "600", color: texto }}>{m.codigoPallet}</span>
                      <p style={{ fontSize: "12px", color: sub, margin: "3px 0 0" }}>{m.tipoMaterial} · {m.motivo}{m.otId ? " · OT: " + m.otId : ""}</p>
                    </div>
                    <span style={{ fontWeight: "800", color: C.red }}>−{m.cantidad} {m.unidad}</span>
                  </div>
                  <div style={{ fontSize: "10px", color: sub, marginTop: "6px" }}>{new Date(m.fecha).toLocaleString("es-AR")} · {m.por}</div>
                </div>
              ))
            }
          </>
        )}
        {seccion === "inventario" && (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
              <h3 style={{ margin: 0, fontSize: "14px", fontWeight: "700", color: texto }}>Inventario actual</h3>
              <button onClick={exportarCSV} style={{ padding: "7px 14px", background: C.green, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "12px", fontWeight: "700" }}>⬇️ Exportar CSV</button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ background: oscuro ? "#0a1f5c" : "#f8fafc" }}>
                    {["Pallet","Tipo","Proveedor","Lote","Actual","Inicial","Unidad","Estado","FSC"].map(h => (
                      <th key={h} style={{ padding: "8px", textAlign: "left", color: sub, fontWeight: "700", borderBottom: "1px solid " + borde }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pallets.map(p => (
                    <tr key={p.id} style={{ borderBottom: "0.5px solid " + borde }}>
                      <td style={{ padding: "6px 8px", color: C.purple, fontWeight: "700" }}>{p.codigoPallet}</td>
                      <td style={{ padding: "6px 8px", color: texto }}>{p.tipoMaterial}</td>
                      <td style={{ padding: "6px 8px", color: sub }}>{p.proveedor}</td>
                      <td style={{ padding: "6px 8px", color: sub }}>{p.lote || "—"}</td>
                      <td style={{ padding: "6px 8px", fontWeight: "700", color: C.green }}>{p.cantidadActual ?? p.cantidadInicial}</td>
                      <td style={{ padding: "6px 8px", color: sub }}>{p.cantidadInicial}</td>
                      <td style={{ padding: "6px 8px", color: sub }}>{p.unidad}</td>
                      <td style={{ padding: "6px 8px", color: estadoPalletColor[p.estado], fontWeight: "600" }}>{estadoPalletLabel[p.estado]}</td>
                      <td style={{ padding: "6px 8px" }}>{p.esFSC ? "🌿" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        {seccion === "reposicion" && (
          <>
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: "1px solid " + (oscuro ? "#7B5BF2" : "#d4c8ff") }}>
              <h3 style={{ fontSize: "13px", fontWeight: "700", color: C.purple, margin: "0 0 6px" }}>🎯 Punto de reposición automático</h3>
              <p style={{ fontSize: "12px", color: sub, margin: 0 }}>
                Marcá como crítico cada tipo de material que no puede faltar en producción. El sistema avisa solo (campanita + panel) cuando el stock toca el punto de reposición — no hace falta acordarse.
              </p>
            </div>
            {tiposUnicos.length === 0
              ? <div style={{ textAlign: "center", padding: "3rem" }}><p style={{ fontSize: "40px" }}>🎯</p><p style={{ color: texto }}>Todavía no hay tipos de material cargados en Stock</p></div>
              : tiposUnicos.map(tipo => {
                  const config = configs.find(c => c.tipoMaterial === tipo) || { critico: false };
                  const estado = config.critico ? calcularEstadoReposicion(tipo, pallets, movs, configs) : null;
                  const stockActualTotal = pallets.filter(p => p.tipoMaterial === tipo && p.estado !== "agotado").reduce((acc, p) => acc + parseFloat(p.cantidadActual ?? p.cantidadInicial ?? 0), 0);
                  // Si ya hay una OC en curso para este material, el semáforo
                  // deja de mostrar el rojo fijo y pasa a mostrar el estado
                  // real de esa OC (leído directo de Compras) -- esta es la
                  // "devolución" que faltaba: ya no queda como una alerta
                  // muda para siempre una vez que se generó la compra.
                  const compraVinculada = config.ocVinculada ? storage.get("alqos_compras").find(c => c.codigo === config.ocVinculada) : null;
                  const colorSemaforo = compraVinculada ? (ESTADOS_OC_REPOSICION[compraVinculada.estado]?.color || sub) : !estado ? sub : estado.estado === "critico" ? C.red : estado.estado === "alerta" ? C.amber : C.green;
                  const labelSemaforo = compraVinculada ? (ESTADOS_OC_REPOSICION[compraVinculada.estado]?.label || `OC ${config.ocVinculada}`) : !config.critico ? "No crítico" : !estado ? "OK" : estado.estado === "critico" ? "🔴 Reponer ya" : estado.estado === "alerta" ? "🟡 Se acerca" : "🟢 OK";
                  return (
                    <div key={tipo} style={{ ...card, border: "1px solid " + (config.critico ? colorSemaforo + "44" : borde) }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <span style={{ fontWeight: "700", color: texto, fontSize: "14px" }}>{tipo}</span>
                          <p style={{ fontSize: "12px", color: sub, margin: "3px 0 0" }}>Stock actual: <strong style={{ color: texto }}>{stockActualTotal}</strong></p>
                          {config.critico && estado && (
                            <p style={{ fontSize: "11px", color: sub, margin: "2px 0 0" }}>
                              Punto de reposición: <strong>{Math.round(estado.puntoReorden)}</strong>
                              {config.modoCalculo === "automatico" && ` (consumo prom. semanal: ${Math.round(estado.consumoSemanal)})`}
                            </p>
                          )}
                          {compraVinculada && (
                            <p style={{ fontSize: "11px", color: C.purple, margin: "2px 0 0", fontWeight: "700" }}>
                              🛒 {compraVinculada.codigo} — generada automáticamente
                            </p>
                          )}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <span style={{ fontSize: "12px", fontWeight: "700", color: colorSemaforo }}>{labelSemaforo}</span>
                          {(esAdmin || usuario?.rol === "Compras") && config.critico && estado && estado.estado !== "ok" && !compraVinculada && (
                            <button onClick={() => generarOCReposicion(config, estado)}
                              style={{ padding: "6px 12px", background: C.green + "15", border: "1px solid " + C.green + "40", color: C.green, borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>
                              🛒 Generar OC ahora
                            </button>
                          )}
                          {esAdmin && (
                            <button onClick={() => setModalConfig({ tipoMaterial: tipo, ...config })}
                              style={{ padding: "6px 12px", background: C.purple + "15", border: "1px solid " + C.purple + "40", color: C.purple, borderRadius: "8px", cursor: "pointer", fontSize: "11px", fontWeight: "700" }}>
                              ⚙️ Configurar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
            }
          </>
        )}
      </div>
      {modalNuevo && <ModalNuevoPallet onGuardar={crearPallet} onCerrar={() => setModalNuevo(false)} oscuro={oscuro} cardBg={cardBg} texto={texto} sub={sub} borde={borde} inp={inp}/>}
      {modalDescuento && <ModalDescuento pallet={modalDescuento} ots={ots} onGuardar={(d) => descontarStock(modalDescuento.id, d)} onCerrar={() => setModalDescuento(null)} oscuro={oscuro} cardBg={cardBg} texto={texto} sub={sub} borde={borde} inp={inp} C={C}/>}
      {modalRotulo && <ModalRotulo pallet={modalRotulo} onCerrar={() => setModalRotulo(null)} cardBg={cardBg} texto={texto} sub={sub} borde={borde} C={C}/>}
      {modalConfig && <ModalConfigReposicion config={modalConfig} onGuardar={(campos) => guardarConfigTipo(modalConfig.tipoMaterial, campos)} onCerrar={() => setModalConfig(null)} oscuro={oscuro} cardBg={cardBg} texto={texto} sub={sub} borde={borde} inp={inp} C={C}/>}
    </div>
  );
}
function ModalNuevoPallet({ onGuardar, onCerrar, oscuro, cardBg, texto, sub, borde, inp }) {
  const CONTENEDORES = [
    { id:"PAL", label:"Pallet",       icono:"📦" },
    { id:"BOB", label:"Bobina",       icono:"🧻" },
    { id:"TAC", label:"Tacho/Bidon",  icono:"🪣" },
    { id:"CAJ", label:"Caja",         icono:"📫" },
    { id:"TAM", label:"Tambor",       icono:"🛢️" },
    { id:"RES", label:"Resma",        icono:"📋" },
    { id:"UNI", label:"Unidad suelta",icono:"🔧" },
  ];
  const TIPOS_BASE = ["Papel","Carton macizo","Cartulina","Plancha offset","Tinta","Barniz","Adhesivo","Film","Insumo quimico","Otro"];
  const UNIDADES = ["kg","pliegos","resmas","litros","unidades","metros","bobinas","tachos","tambores","cajas"];
  const [tiposCustom, setTiposCustom] = useState(() => {
    try { return JSON.parse(localStorage.getItem("alqos_tipos_material") || "[]"); } catch { return []; }
  });
  const TIPOS = [...TIPOS_BASE, ...tiposCustom];
  const [form, setForm] = useState({ tipoMaterial: "Carton macizo", contenedor: "PAL", unidad: "pliegos", cantidadInicial: "", proveedor: "", lote: "", gramaje: "", medida: "", esFSC: false, fechaIngreso: new Date().toISOString().slice(0,10), codigoIngreso: "", remito: "" });
  const [nuevoTipo, setNuevoTipo] = useState("");
  const lbl = { fontSize: "11px", color: sub, fontWeight: "700", display: "block", marginTop: "12px", textTransform: "uppercase" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900, padding: "20px" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "24px", maxWidth: "500px", width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: texto }}>Nuevo pallet / lote</h3>
          <button onClick={onCerrar} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: sub }}>✕</button>
        </div>
        <span style={lbl}>Tipo de contenedor *</span>
        <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginTop:"6px" }}>
          {CONTENEDORES.map(c => (
            <button key={c.id} type="button" onClick={() => setForm(f => ({ ...f, contenedor: c.id }))}
              style={{ padding:"6px 12px", borderRadius:"8px", fontSize:"12px", cursor:"pointer", fontWeight:"600", border: form.contenedor === c.id ? "2px solid #7B5BF2" : "1px solid " + borde, background: form.contenedor === c.id ? "#7B5BF215" : cardBg, color: form.contenedor === c.id ? "#7B5BF2" : sub }}>
              {c.icono} {c.label}
            </button>
          ))}
        </div>
        <span style={lbl}>Tipo de material *</span>
        <select value={form.tipoMaterial} onChange={e => setForm(f => ({ ...f, tipoMaterial: e.target.value }))} style={{ ...inp, marginBottom:"4px" }}>
          {TIPOS.map(t => <option key={t}>{t}</option>)}
          <option value="__nuevo__">+ Agregar nuevo tipo...</option>
        </select>
        {(form.tipoMaterial === "__nuevo__" || nuevoTipo) && (
          <div style={{ display:"flex", gap:"6px", marginTop:"4px" }}>
            <input value={nuevoTipo} onChange={e => setNuevoTipo(e.target.value)} style={{ ...inp, flex:1 }} placeholder="Ej: Papel tissue, Hilo, Cartón corrugado..."/>
            <button type="button" onClick={() => {
              if (!nuevoTipo.trim()) return;
              const nuevos = [...tiposCustom, nuevoTipo.trim()];
              setTiposCustom(nuevos);
              localStorage.setItem("alqos_tipos_material", JSON.stringify(nuevos));
              subirDatos("alqos_tipos_material");
              setForm(f => ({ ...f, tipoMaterial: nuevoTipo.trim() }));
              setNuevoTipo("");
            }} style={{ padding:"8px 14px", background:"#7B5BF2", color:"white", border:"none", borderRadius:"8px", cursor:"pointer", fontWeight:"700", fontSize:"12px", whiteSpace:"nowrap" }}>
              + Guardar
            </button>
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "10px" }}>
          <div><span style={lbl}>Cantidad *</span><input type="number" value={form.cantidadInicial} onChange={e => setForm(f => ({ ...f, cantidadInicial: e.target.value }))} style={inp} placeholder="0"/></div>
          <div><span style={lbl}>Unidad</span><select value={form.unidad} onChange={e => setForm(f => ({ ...f, unidad: e.target.value }))} style={inp}>{UNIDADES.map(u => <option key={u}>{u}</option>)}</select></div>
        </div>
        <span style={lbl}>Proveedor *</span>
        <input value={form.proveedor} onChange={e => setForm(f => ({ ...f, proveedor: e.target.value }))} style={inp} placeholder="Nombre del proveedor"/>
        <span style={lbl}>Lote / Partida</span>
        <input value={form.lote} onChange={e => setForm(f => ({ ...f, lote: e.target.value }))} style={inp} placeholder="N de lote"/>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          <div><span style={lbl}>{TIPOS_CALIBRE.includes(form.tipoMaterial) ? "Calibre" : "Gramaje g/m2"}</span><input type="number" step={TIPOS_CALIBRE.includes(form.tipoMaterial) ? "0.1" : "1"} value={form.gramaje} onChange={e => setForm(f => ({ ...f, gramaje: e.target.value }))} style={inp} placeholder={TIPOS_CALIBRE.includes(form.tipoMaterial) ? "1.5" : "300"}/></div>
          <div><span style={lbl}>Medida</span><input value={form.medida} onChange={e => setForm(f => ({ ...f, medida: e.target.value }))} style={inp} placeholder="70x100"/></div>
        </div>
        <span style={lbl}>N° de Remito</span>
        <input value={form.remito} onChange={e => setForm(f => ({ ...f, remito: e.target.value }))} style={inp} placeholder="N° remito del proveedor"/>
        <span style={lbl}>Codigo ingreso (IM-XXXX)</span>
        <input value={form.codigoIngreso} onChange={e => setForm(f => ({ ...f, codigoIngreso: e.target.value }))} style={inp} placeholder="Opcional"/>
        <span style={lbl}>Fecha ingreso</span>
        <input type="date" value={form.fechaIngreso} onChange={e => setForm(f => ({ ...f, fechaIngreso: e.target.value }))} style={inp}/>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "14px", cursor: "pointer", fontSize: "13px", color: texto }}>
          <input type="checkbox" checked={form.esFSC} onChange={e => setForm(f => ({ ...f, esFSC: e.target.checked }))} style={{ width: "16px", height: "16px" }}/>
          Material FSC certificado
        </label>
        <button onClick={() => { if (!form.cantidadInicial || !form.proveedor?.trim()) { alert("Completa proveedor y cantidad"); return; } onGuardar(form); }}
          style={{ width: "100%", marginTop: "20px", padding: "12px", background: "linear-gradient(135deg,#7B5BF2,#09184D)", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
          Crear pallet
        </button>
      </div>
    </div>
  );
}
function ModalDescuento({ pallet, ots, onGuardar, onCerrar, cardBg, texto, sub, borde, inp, C }) {
  const [form, setForm] = useState({ cantidad: "", motivo: "OT", otId: "", observaciones: "" });
  const disponible = parseFloat(pallet.cantidadActual ?? pallet.cantidadInicial ?? 0);
  const lbl = { fontSize: "11px", color: sub, fontWeight: "700", display: "block", marginTop: "12px", textTransform: "uppercase" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900, padding: "20px" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "24px", maxWidth: "420px", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: texto }}>Descontar stock</h3>
          <button onClick={onCerrar} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: sub }}>✕</button>
        </div>
        <div style={{ background: "#f0f9ff", borderRadius: "10px", padding: "12px", marginBottom: "12px" }}>
          <div style={{ fontSize: "14px", fontWeight: "700", color: texto }}>{pallet.codigoPallet} — {pallet.tipoMaterial}</div>
          <div style={{ fontSize: "13px", fontWeight: "700", color: C.green, marginTop: "4px" }}>Disponible: {disponible} {pallet.unidad}</div>
        </div>
        <span style={lbl}>Motivo</span>
        <select value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} style={inp}>
          <option value="OT">Orden de Trabajo</option>
          <option value="merma">Merma / desperdicio</option>
          <option value="devolucion">Devolucion a proveedor</option>
          <option value="ajuste">Ajuste de inventario</option>
        </select>
        {form.motivo === "OT" && (
          <>
            <span style={lbl}>Numero de OT</span>
            <input value={form.otId} onChange={e => setForm(f => ({ ...f, otId: e.target.value }))} style={inp} placeholder="OT-2026-001"/>
          </>
        )}
        <span style={lbl}>Cantidad ({pallet.unidad}) *</span>
        <input type="number" value={form.cantidad} onChange={e => setForm(f => ({ ...f, cantidad: e.target.value }))} style={inp} placeholder="0"/>
        <button onClick={() => { const c = parseFloat(form.cantidad); if (!c || c <= 0) { alert("Cantidad invalida"); return; } if (c > disponible) { alert("No hay suficiente stock"); return; } onGuardar({ ...form, cantidad: c }); }}
          style={{ width: "100%", marginTop: "18px", padding: "12px", background: "linear-gradient(135deg,#e63946,#b91c1c)", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
          Confirmar descuento
        </button>
      </div>
    </div>
  );
}
function ModalRotulo({ pallet, onCerrar, cardBg, texto, sub, borde, C }) {
  const svgRef = useRef(null);
  // ALQOS es el nombre del software, no el de la empresa dueña del pallet --
  // este rótulo se imprime y puede ir a otras empresas, así que tiene que
  // mostrar el nombre real del tenant (cacheado en localStorage al loguear,
  // GestorUsuarios.js) en vez de "ALQOS". Componente sin acceso a `usuario`
  // como prop, mismo criterio que BotonInstructivo.js.
  let marcaEmpresa = "ALQOS";
  try {
    const empresa = localStorage.getItem("alqos_empresa") || "";
    if (empresa && empresa !== "ALQOS™") marcaEmpresa = empresa;
  } catch {}
  // Logo opcional (mismo caché base64 que BotonInstructivo.js/IngresoMateriales.js).
  // Es un data URL autocontenido, así que sobrevive el outerHTML que imprimir()
  // copia a la ventana nueva -- no depende de que esa ventana tenga red.
  let logoDataUrl = null;
  try { logoDataUrl = localStorage.getItem("alqos_logo_empresa") || null; } catch {}
  useEffect(() => {
    const cargar = () => new Promise(res => {
      if (window.JsBarcode) { res(); return; }
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js";
      s.onload = res; document.head.appendChild(s);
    });
    cargar().then(() => {
      if (svgRef.current && window.JsBarcode) {
        try { window.JsBarcode(svgRef.current, pallet.codigoPallet, { format: "CODE128", width: 2, height: 60, displayValue: true, fontSize: 13, margin: 4 }); } catch {}
      }
    });
  }, [pallet.codigoPallet]);
  const imprimir = () => {
    const el = document.getElementById("rotulo-contenido");
    if (!el) return;
    const w = window.open("", "", "width=800,height=600");
    w.document.write("<html><head><title>Rotulo " + pallet.codigoPallet + "</title><style>body{margin:20px;font-family:sans-serif;}</style></head><body>" + el.outerHTML + "</body></html>");
    w.document.close();
    setTimeout(() => w.print(), 500);
  };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999, padding: "20px" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "24px", maxWidth: "600px", width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: texto }}>Rotulo — {pallet.codigoPallet}</h3>
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={imprimir} style={{ padding: "8px 16px", background: C.purple, color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "12px" }}>Imprimir</button>
            <button onClick={onCerrar} style={{ padding: "8px 16px", background: "#f1f5f9", color: "#333", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "700", fontSize: "12px" }}>Cerrar</button>
          </div>
        </div>
        <div id="rotulo-contenido" style={{ border: "2px dashed #cbd5e1", borderRadius: "8px", padding: "24px", background: "white" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "3px solid #09184D", paddingBottom: "10px", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {logoDataUrl && <img src={logoDataUrl} alt="" style={{ maxHeight: "28px", maxWidth: "80px", objectFit: "contain" }}/>}
              <div style={{ fontSize: "22px", fontWeight: "900", color: "#09184D" }}>{marcaEmpresa}</div>
            </div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: "#09184D" }}>{pallet.codigoPallet}</div>
          </div>
          <div style={{ textAlign: "center", margin: "0 0 14px" }}>
            <svg ref={svgRef} style={{ maxWidth: "100%", display: "inline-block" }}/>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {[["Tipo", pallet.tipoMaterial],["Proveedor", pallet.proveedor],["Lote", pallet.lote || "—"],["Fecha ingreso", pallet.fechaIngreso],["Cantidad inicial", pallet.cantidadInicial + " " + pallet.unidad],[TIPOS_CALIBRE.includes(pallet.tipoMaterial) ? "Calibre" : "Gramaje", pallet.gramaje ? (TIPOS_CALIBRE.includes(pallet.tipoMaterial) ? pallet.gramaje + " cal." : pallet.gramaje + " g/m2") : "—"],["Medida", pallet.medida || "—"],["Estado", pallet.estado]].map(([k,v]) => (
              <div key={k} style={{ borderBottom: "0.5px solid #e2e8f0", paddingBottom: "5px" }}>
                <div style={{ fontSize: "9px", color: "#94a3b8", textTransform: "uppercase", fontWeight: "700" }}>{k}</div>
                <div style={{ fontSize: "13px", color: "#09184D", fontWeight: "600" }}>{v}</div>
              </div>
            ))}
          </div>
          {pallet.esFSC && <div style={{ marginTop: "12px", background: "#f0fdf4", border: "2px solid #22c55e", borderRadius: "8px", padding: "8px 14px", fontSize: "12px", fontWeight: "800", color: "#15803d" }}>MATERIAL FSC CERTIFICADO</div>}
        </div>
      </div>
    </div>
  );
}
function ModalConfigReposicion({ config, onGuardar, onCerrar, oscuro, cardBg, texto, sub, borde, inp, C }) {
const [form, setForm] = useState({
  critico: config.critico || false,
  modoCalculo: config.modoCalculo || "fijo",
  stockMinimo: config.stockMinimo || "",
  tiempoReposicionDias: config.tiempoReposicionDias || 7,
  proveedorHabitual: config.proveedorHabitual || "",
});
  const lbl = { fontSize: "11px", color: sub, fontWeight: "700", display: "block", marginTop: "12px", textTransform: "uppercase" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 900, padding: "20px" }}>
      <div style={{ background: cardBg, borderRadius: "16px", padding: "24px", maxWidth: "460px", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: texto }}>🎯 {config.tipoMaterial}</h3>
          <button onClick={onCerrar} style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: sub }}>✕</button>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "13px", color: texto, marginBottom: "6px" }}>
          <input type="checkbox" checked={form.critico} onChange={e => setForm(f => ({ ...f, critico: e.target.checked }))} style={{ width: "16px", height: "16px" }}/>
          Es un material crítico (no puede faltar en producción)
        </label>
        {form.critico && (
          <>
            <span style={lbl}>Cómo calcular el punto de reposición</span>
            <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
              <button type="button" onClick={() => setForm(f => ({ ...f, modoCalculo: "fijo" }))}
                style={{ flex: 1, padding: "8px", borderRadius: "8px", fontSize: "12px", cursor: "pointer", fontWeight: "700", border: form.modoCalculo === "fijo" ? "2px solid " + C.purple : "1px solid " + borde, background: form.modoCalculo === "fijo" ? C.purple + "15" : cardBg, color: form.modoCalculo === "fijo" ? C.purple : sub }}>
                Mínimo fijo
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, modoCalculo: "automatico" }))}
                style={{ flex: 1, padding: "8px", borderRadius: "8px", fontSize: "12px", cursor: "pointer", fontWeight: "700", border: form.modoCalculo === "automatico" ? "2px solid " + C.purple : "1px solid " + borde, background: form.modoCalculo === "automatico" ? C.purple + "15" : cardBg, color: form.modoCalculo === "automatico" ? C.purple : sub }}>
                Cálculo automático
              </button>
            </div>
            {form.modoCalculo === "fijo" ? (
              <>
                <span style={lbl}>Stock mínimo (avisar por debajo de esto)</span>
                <input type="number" value={form.stockMinimo} onChange={e => setForm(f => ({ ...f, stockMinimo: e.target.value }))} style={inp} placeholder="Ej: 50"/>
              </>
            ) : (
              <>
                <span style={lbl}>Tiempo de reposición del proveedor (días)</span>
                <input type="number" value={form.tiempoReposicionDias} onChange={e => setForm(f => ({ ...f, tiempoReposicionDias: e.target.value }))} style={inp} placeholder="Ej: 7"/>
                <p style={{ fontSize: "11px", color: sub, marginTop: "6px" }}>El sistema calcula el punto de reposición solo, mirando el consumo de las últimas 4 semanas de este material (según los descuentos de stock registrados) y cuánto tarda en llegar el proveedor.</p>
              </>
           )}
            <span style={lbl}>Proveedor habitual (para la OC automática)</span>
            <input value={form.proveedorHabitual} onChange={e => setForm(f => ({ ...f, proveedorHabitual: e.target.value }))} style={inp} placeholder="Nombre del proveedor que siempre le comprás esto"/>
          </>
        )}
        <button onClick={() => onGuardar(form)}
          style={{ width: "100%", marginTop: "18px", padding: "12px", background: "linear-gradient(135deg,#7B5BF2,#09184D)", color: "white", border: "none", borderRadius: "10px", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}>
          Guardar configuración
        </button>
      </div>
    </div>
  );
}
